import dayjs from 'dayjs';

/**
 * 工作日计算工具函数
 */

/**
 * 判断是否为工作日（周一到周五）
 * @param {dayjs.Dayjs} date - 日期对象
 * @returns {boolean} 是否为工作日
 */
export const isWorkday = (date) => {
  const day = date.day(); // 0=周日, 1=周一, ..., 6=周六
  return day >= 1 && day <= 5;
};

/**
 * 获取下一个工作日
 * @param {dayjs.Dayjs} date - 起始日期
 * @returns {dayjs.Dayjs} 下一个工作日
 */
export const getNextWorkday = (date) => {
  let nextDay = date.add(1, 'day');
  while (!isWorkday(nextDay)) {
    nextDay = nextDay.add(1, 'day');
  }
  return nextDay;
};

/**
 * 获取指定日期后的第N个工作日
 * @param {dayjs.Dayjs} startDate - 起始日期
 * @param {number} workdays - 工作日数量
 * @returns {dayjs.Dayjs} 目标日期
 */
export const addWorkdays = (startDate, workdays) => {
  let currentDate = startDate;
  let remainingDays = workdays;
  
  while (remainingDays > 0) {
    currentDate = currentDate.add(1, 'day');
    if (isWorkday(currentDate)) {
      remainingDays--;
    }
  }
  
  return currentDate;
};

/**
 * 根据工时计算任务结束日期（按每天8小时工作制）
 * @param {dayjs.Dayjs} startDate - 开始日期
 * @param {number} estimatedHours - 预估工时
 * @returns {dayjs.Dayjs} 结束日期
 */
export const calculateEndDateByHours = (startDate, estimatedHours) => {
  if (!estimatedHours || estimatedHours <= 0) {
    return startDate;
  }
  
  // 按每天8小时计算需要的工作日数
  const workdaysNeeded = Math.ceil(estimatedHours / 8);
  
  // 如果开始日期不是工作日，先找到下一个工作日
  let actualStartDate = isWorkday(startDate) ? startDate : getNextWorkday(startDate);
  
  // 计算结束日期（减1是因为开始日期也算一个工作日）
  if (workdaysNeeded <= 1) {
    return actualStartDate;
  } else {
    return addWorkdays(actualStartDate, workdaysNeeded - 1);
  }
};

/**
 * 获取任务完成后的第二个工作日
 * @param {dayjs.Dayjs} taskEndDate - 任务结束日期
 * @returns {dayjs.Dayjs} 第二个工作日
 */
export const getSecondWorkdayAfter = (taskEndDate) => {
  // 先获取任务结束日期的下一个工作日
  const firstWorkday = getNextWorkday(taskEndDate);
  // 再获取第二个工作日
  return getNextWorkday(firstWorkday);
};

/**
 * 获取当前日期，如果是非工作日则返回下一个工作日
 * @returns {dayjs.Dayjs} 当前工作日
 */
export const getCurrentWorkday = () => {
  const today = dayjs();
  return isWorkday(today) ? today : getNextWorkday(today);
};

/**
 * 计算两个日期（含端点）之间的工作日天数
 * 说明：
 * - 入参可为 dayjs 对象或可被 dayjs 解析的日期
 * - 仅计入周一至周五，跳过周末
 * - 若 start > end，会自动对调
 * @param {dayjs.Dayjs|Date|string|number} startDate 起始日期
 * @param {dayjs.Dayjs|Date|string|number} endDate 结束日期
 * @returns {number} 工作日总数
 */
export const countWorkdaysInclusive = (startDate, endDate) => {
  const start = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).startOf('day');
  if (!start.isValid() || !end.isValid()) return 0;

  let from = start;
  let to = end;
  if (from.isAfter(to)) {
    const tmp = from;
    from = to;
    to = tmp;
  }

  let count = 0;
  let cur = from;
  while (cur.isBefore(to) || cur.isSame(to, 'day')) {
    if (isWorkday(cur)) count++;
    cur = cur.add(1, 'day');
  }
  return count;
};